import { useMutation } from '@tanstack/react-query';
import {
  acceptInvite,
  acceptInviteLink,
  completeProfile,
  exchangeMagicLinkToken,
  exchangeOAuthToken,
  loginWithPassword,
  requestMagicLink,
  requestPasswordReset,
  resetPassword,
  signupWithPassword,
  verifySignupOtp,
  resendSignupOtp,
  verifyMagicCode,
  type AuthResponse,
  type AuthResult,
} from '@/lib/auth';

/**
 * One-shot auth mutations. None of them need caching — the entire purpose is
 * to obtain a fresh session and then navigate. We expose them as
 * `useMutation` hooks so callers benefit from a unified `isPending`/`isError`
 * surface, and StrictMode double-invocation can be guarded by the caller
 * with a single `didRun` ref before calling `.mutate(...)`.
 */

export function useExchangeOAuthTokenMutation() {
  return useMutation<AuthResult, Error, string>({
    mutationFn: (token: string) => exchangeOAuthToken(token),
  });
}

export function useExchangeMagicLinkTokenMutation() {
  return useMutation<AuthResult, Error, string>({
    mutationFn: (token: string) => exchangeMagicLinkToken(token),
  });
}

export function useAcceptInviteMutation() {
  return useMutation<AuthResult, Error, string>({
    mutationFn: (token: string) => acceptInvite(token),
  });
}

export function useAcceptInviteLinkMutation() {
  return useMutation<AuthResponse, Error, string>({
    mutationFn: (token: string) => acceptInviteLink(token),
  });
}

type CompleteProfilePayload = {
  pendingToken: string;
  name: string;
  timezone?: string;
};

export function useCompleteProfileMutation() {
  return useMutation<AuthResponse, Error, CompleteProfilePayload>({
    mutationFn: ({ pendingToken, name, timezone }) =>
      completeProfile(pendingToken, name, timezone),
  });
}

type RequestMagicLinkPayload = {
  email: string;
  inviteToken?: string;
  shareableLinkToken?: string;
  /** Cloudflare Turnstile token; omitted when the widget is disabled. */
  captchaToken?: string;
};

export function useRequestMagicLinkMutation() {
  return useMutation<void, Error, RequestMagicLinkPayload>({
    // The only auth mutation where the user stays on the page, so a toast is
    // the feedback. The session-creating mutations navigate away on success —
    // a toast there would mount on an unmounting route, so they get none.
    meta: { successMessage: 'Check your email for a sign-in link' },
    mutationFn: ({ email, inviteToken, shareableLinkToken, captchaToken }) =>
      requestMagicLink(email, inviteToken, shareableLinkToken, captchaToken),
  });
}

type VerifyMagicCodePayload = {
  email: string;
  code: string;
  /** Seamless re-auth (TRU-301) — restore the exact workspace the user was on. */
  workspaceId?: number;
  /**
   * "Keep me signed in". Drives both the backend session TTL (absent/false =
   * 1 day, true = 30 days) and whether tokens are mirrored to `localStorage`.
   * Absent = no preference — leaves this tab's existing persistence mode alone.
   */
  rememberMe?: boolean;
};

export function useVerifyMagicCodeMutation() {
  return useMutation<AuthResult, Error, VerifyMagicCodePayload>({
    mutationFn: ({ email, code, workspaceId, rememberMe }) =>
      verifyMagicCode(email, code, workspaceId, rememberMe),
  });
}

type PasswordLoginPayload = {
  email: string;
  password: string;
  /** Seamless re-auth (TRU-301) — restore the exact workspace the user was on. */
  workspaceId?: number;
  /** "Keep me signed in" — drives the backend session TTL and token persistence. */
  rememberMe?: boolean;
  /** Cloudflare Turnstile token; omitted when the widget is disabled. */
  captchaToken?: string;
};

/**
 * Password login. Resolves to a full `AuthResult` — there is no OTP step, so
 * the caller stores the session and navigates exactly as it would after a
 * successful `useVerifyMagicCodeMutation`.
 */
export function useLoginWithPasswordMutation() {
  return useMutation<AuthResult, Error, PasswordLoginPayload>({
    mutationFn: ({ email, password, workspaceId, rememberMe, captchaToken }) =>
      loginWithPassword(email, password, workspaceId, rememberMe, captchaToken),
  });
}

type RequestPasswordResetPayload = { email: string; captchaToken?: string };

export function useRequestPasswordResetMutation() {
  return useMutation<void, Error, RequestPasswordResetPayload>({
    mutationFn: ({ email, captchaToken }) =>
      requestPasswordReset(email, captchaToken),
  });
}

type ResetPasswordPayload = {
  token: string;
  password: string;
};

export function useResetPasswordMutation() {
  return useMutation<void, Error, ResetPasswordPayload>({
    mutationFn: ({ token, password }) => resetPassword(token, password),
  });
}

type SignupWithPasswordPayload = {
  email: string;
  password: string;
  captchaToken?: string;
};

/** Step 1 of password signup — sends a code, does NOT create a session. */
export function useSignupWithPasswordMutation() {
  return useMutation<void, Error, SignupWithPasswordPayload>({
    mutationFn: ({ email, password, captchaToken }) =>
      signupWithPassword(email, password, captchaToken),
  });
}

type VerifySignupOtpPayload = { email: string; code: string };

export function useVerifySignupOtpMutation() {
  return useMutation<AuthResult, Error, VerifySignupOtpPayload>({
    mutationFn: ({ email, code }) => verifySignupOtp(email, code),
  });
}

type ResendSignupOtpPayload = { email: string; captchaToken?: string };

export function useResendSignupOtpMutation() {
  return useMutation<void, Error, ResendSignupOtpPayload>({
    mutationFn: ({ email, captchaToken }) => resendSignupOtp(email, captchaToken),
  });
}
